/**
 * Docs loader — fetches FVW + Pact markdown for the /docs section.
 *
 * Strategy:
 * 1. For FVW docs: try live (raw.githubusercontent.com) with 5s timeout, fall back to bundled
 * 2. For Pact docs: always from bundled (they're in this repo, not in the gallery)
 * 3. Returns {content, source: 'live' | 'bundled'}
 */

const FVW_BASE_LIVE = 'https://raw.githubusercontent.com/avidtech6/fv-module-gallery/main/gallery-pact/fvw';
const FVW_BASE_BUNDLED = '/docs/fvw';

const PACT_BASE = '/docs/pact'; // always bundled

export type DocSource = 'live' | 'bundled' | 'error';

export interface DocResult {
  content: string;
  source: DocSource;
  path: string;
  error?: string;
}

async function fetchWithTimeout(url: string, ms = 5000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const res = await fetch(url, { signal: controller.signal });
    return res;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch an FVW section.
 * Tries live first, falls back to bundled.
 */
export async function fetchFvwSection(section: string): Promise<DocResult> {
  // section is e.g. "01-app-constitution"
  const filename = section.endsWith('.md') ? section : `${section}.md`;
  const liveUrl = `${FVW_BASE_LIVE}/${filename}`;
  const bundledUrl = `${FVW_BASE_BUNDLED}/${filename}`;

  try {
    const res = await fetchWithTimeout(liveUrl, 5000);
    if (res.ok) {
      const content = await res.text();
      return { content, source: 'live', path: liveUrl };
    }
    throw new Error(`HTTP ${res.status}`);
  } catch (e) {
    // Fall back to bundled
    const res = await fetch(bundledUrl);
    if (res.ok) {
      const content = await res.text();
      return { content, source: 'bundled', path: bundledUrl };
    }
    return {
      content: `# Error loading section\n\nCould not load FVW section: \`${section}\`\n\nLive: ${liveUrl}\nBundled: ${bundledUrl}\n\nError: ${e instanceof Error ? e.message : String(e)}`,
      source: 'error',
      path: bundledUrl,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

/**
 * Fetch a Pact doc (always bundled).
 */
export async function fetchPactDoc(doc: string): Promise<DocResult> {
  // doc is e.g. "invariants" or "rules/registry-rules"
  const filename = doc.endsWith('.md') ? doc : `${doc}.md`;
  const url = `${PACT_BASE}/${filename}`;
  try {
    const res = await fetch(url);
    if (res.ok) {
      const content = await res.text();
      return { content, source: 'bundled', path: url };
    }
    throw new Error(`HTTP ${res.status}`);
  } catch (e) {
    return {
      content: `# Error loading pact doc\n\nCould not load: \`${doc}\`\n\nURL: ${url}\n\nError: ${e instanceof Error ? e.message : String(e)}`,
      source: 'error',
      path: url,
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

/**
 * List the FVW sections (from the bundled copy).
 * Returns section names like "00-philosophy", "01-app-constitution", etc.
 */
export async function listFvwSections(): Promise<string[]> {
  // Hard-coded list because we can't list a directory in a static SPA.
  // Keep this in sync with the FVW v8 canon.
  return [
    '00-philosophy',
    '01-app-constitution',
    '02-folder-layout',
    '08-anti-drift',
    '10-modules-as-clusters',
    '11-recipe-book',
    '17-modules-and-tiers',
    '22-behaviour-first-codex',
  ];
}

/**
 * List the Pact docs (from the bundled copy).
 */
export async function listPactDocs(): Promise<string[]> {
  return [
    'app-pact',
    'invariants',
    'anti-drift',
    'rules/registry-rules',
    'rules/ui-rules',
    'rules/state-rules',
    'rules/deploy-rules',
    'rules/security-rules',
    'rules/rebuild-rules',
  ];
}
